export interface BuildFullEmbedScriptParams {
    embedScriptUrl: string;
    companySitepath: string;
    configId: string | number;
    configuration: unknown;
    fitViewport: boolean;
}

export const buildFullEmbedScript = ({
    embedScriptUrl,
    companySitepath,
    configId,
    configuration,
    fitViewport,
}: BuildFullEmbedScriptParams): string => {
    const configJson = JSON.stringify(configuration, null, 2)
        .split('\n')
        .map((line, i) => (i === 0 ? line : '    ' + line))
        .join('\n');
    const divId = `bokamera-embedded-${String(configId).slice(-3)}-inl`.toLowerCase();
    return `<script src="${embedScriptUrl}" async onerror="console.error('BokaMera: widget failed to load')"></script>
<script>
  window.bmq = window.bmq || [];
  function bm() { bmq.push(arguments); }
  bm('js', new Date());
  bm('config', 'widget', {
    kind: 'book',
    companySitepath: '${companySitepath}',
    target: '#${divId}',
    fitViewport: ${fitViewport},
    widgetConfiguration: ${configJson},
  });
</script>

<div id="${divId}"></div>`;
};

export type EmbedLanguage = 'sv' | 'en' | 'fi' | 'no';

export interface EmbedTopOffset {
    desktop?: number;
    mobile?: number;
}

export interface CustomerData {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
}

export interface EmbedSnippetParams {
    embedScriptUrl: string;
    companySitepath: string;
    configId: string;
    fitViewport: boolean;
    language?: EmbedLanguage;
    topOffset?: EmbedTopOffset;
}

export type ArticlesExample = 'helper' | 'synced';

export interface ArticlesSnippetParams extends EmbedSnippetParams {
    example: ArticlesExample;
    articleId: number | null;
    syncedArticleIds: number[];
}

export interface CustomerSnippetParams extends EmbedSnippetParams {
    customer: CustomerData;
}

export type PricesExample = 'helper' | 'synced';

export interface PricesSnippetParams extends EmbedSnippetParams {
    pricesExample: PricesExample;
    priceId: number | null;
    syncedPriceIds: number[];
}

export type EmbedLevel = 'basic' | 'articles' | 'customer' | 'prices';

const PLACEHOLDER_ARTICLE_ID = 622;

const UNPRICED_PRICE_ID = 0;

const PLACEHOLDER_SPOT_COUNT = 2;

const PLACEHOLDER_SYNCED_ARTICLE_IDS = [622, 624];

const PLACEHOLDER_SYNCED_PRICE_IDS = [812, 813];

const MIN_SYNCED_ARTICLES = 2;

const MIN_SYNCED_PRICES = 2;

const CUSTOMER_FIELDS: (keyof CustomerData)[] = ['firstName', 'lastName', 'email', 'phone'];

export const buildBasicEmbedScript = (params: EmbedSnippetParams): string =>
    wrapSnippet(params, [buildConfigCommand(params)]);

export const buildArticlesEmbedScript = (params: ArticlesSnippetParams): string => {
    if (params.example === 'synced') return buildSyncedAddOnsEmbedScript(params);
    return wrapSnippet(params, [
        buildConfigCommand(params),
        buildSelectedArticlesListener(params.configId),
        buildSetArticleQuantityHelper(params.configId, params.articleId),
    ]);
};

const buildSyncedAddOnsEmbedScript = (params: ArticlesSnippetParams): string =>
    wrapSnippet(params, [
        buildConfigCommand(params),
        buildSyncedAddOnsListener(params.configId, params.syncedArticleIds),
    ]);

export const resolveSyncedArticleIds = (syncedArticleIds: number[]): number[] =>
    syncedArticleIds.length >= MIN_SYNCED_ARTICLES
        ? syncedArticleIds
        : PLACEHOLDER_SYNCED_ARTICLE_IDS;

export const buildCustomerEmbedScript = (params: CustomerSnippetParams): string =>
    wrapSnippet(params, [
        buildConfigCommand(params),
        buildSetCustomerDataCommand(params.configId, params.customer),
    ]);

export const buildPricesEmbedScript = (params: PricesSnippetParams): string => {
    if (params.pricesExample === 'synced') return buildSyncedPricesEmbedScript(params);
    return wrapSnippet(params, [
        buildConfigCommand(params),
        buildPriceQuantitiesListener(params.configId),
        buildSetPriceQuantityHelper(params.configId, params.priceId),
    ]);
};

const buildSyncedPricesEmbedScript = (params: PricesSnippetParams): string =>
    wrapSnippet(params, [
        buildConfigCommand(params),
        buildSyncedPricesListener(params.configId, params.syncedPriceIds),
    ]);

export const resolveSyncedPriceIds = (syncedPriceIds: number[]): number[] =>
    syncedPriceIds.length >= MIN_SYNCED_PRICES ? syncedPriceIds : PLACEHOLDER_SYNCED_PRICE_IDS;

export const buildEmbedScriptForLevel = (
    level: EmbedLevel,
    params: ArticlesSnippetParams & CustomerSnippetParams & PricesSnippetParams
): string => {
    if (level === 'articles') return buildArticlesEmbedScript(params);
    if (level === 'customer') return buildCustomerEmbedScript(params);
    if (level === 'prices') return buildPricesEmbedScript(params);
    return buildBasicEmbedScript(params);
};

export const buildEmbedDivId = (configId: string): string =>
    `bokamera-embedded-${configId.slice(-3)}`.toLowerCase();

const wrapSnippet = (params: EmbedSnippetParams, blocks: string[]): string => {
    const divId = buildEmbedDivId(params.configId);
    return `${buildScriptTag(params.embedScriptUrl)}
<script>
  window.bmq = window.bmq || [];
  function bm() { bmq.push(arguments); }
  bm('js', new Date());
${blocks.join('\n\n')}
</script>

<div id="${divId}"></div>`;
};

const buildScriptTag = (embedScriptUrl: string): string =>
    `<script src="${embedScriptUrl}" async onerror="console.error('BokaMera: widget failed to load')"></script>`;

const buildConfigCommand = (params: EmbedSnippetParams): string => {
    const lines = buildConfigOptionLines(params, buildEmbedDivId(params.configId));
    return `  bm('config', '${params.configId}', {\n${indentLines(lines)}\n  });`;
};

const buildConfigOptionLines = (params: EmbedSnippetParams, divId: string): string[] => {
    const topOffset = formatTopOffset(params.topOffset);
    return [
        `kind: 'book',`,
        `companySitepath: '${params.companySitepath}',`,
        params.language ? `language: '${params.language}',` : null,
        `target: '#${divId}',`,
        `fitViewport: ${params.fitViewport},`,
        topOffset ? `topOffset: ${topOffset},` : null,
    ].filter(isPresent);
};

const formatTopOffset = (topOffset?: EmbedTopOffset): string | null => {
    const { desktop, mobile } = topOffset ?? {};
    const entries = [
        desktop !== undefined ? `desktop: ${desktop}` : null,
        mobile !== undefined ? `mobile: ${mobile}` : null,
    ].filter(isPresent);
    return entries.length > 0 ? `{ ${entries.join(', ')} }` : null;
};

const buildSelectedArticlesListener = (configId: string): string =>
    `  bm('on', '${configId}', 'selectedArticlesChange', function (payload) {
    var addOns = payload.articles.filter(function (article) { return article.Duration === 0; });
    var durationOptions = payload.articles.filter(function (article) { return article.Duration > 0; });
    console.log('BokaMera selected add-ons', addOns, 'duration options', durationOptions);
  });`;

const buildSetArticleQuantityHelper = (configId: string, articleId: number | null): string =>
    `  // setArticleQuantity is not queued: call it after onReady and after the visitor
  // has selected a service (for example from your own UI or the first event above).
  function bokameraSetArticleQuantity(articleId, quantity) {
    bm('setArticleQuantity', '${configId}', { articleId: articleId, quantity: quantity });
  }
  ${buildSetArticleQuantityExample(articleId)}`;

const buildSetArticleQuantityExample = (articleId: number | null): string => {
    if (articleId === null) {
        return `// Example: bokameraSetArticleQuantity(${PLACEHOLDER_ARTICLE_ID}, 1); // replace ${PLACEHOLDER_ARTICLE_ID} with your article id`;
    }
    return `// Example: bokameraSetArticleQuantity(${articleId}, 1);`;
};

const buildPriceQuantitiesListener = (configId: string): string =>
    `  bm('on', '${configId}', 'priceQuantitiesChange', function (payload) {
    console.log('BokaMera price quantities', payload.quantities);
  });`;

const buildSetPriceQuantityHelper = (configId: string, priceId: number | null): string =>
    `  // Send after the visitor has picked a time — price ids can change when the time is chosen.
  // priceId 0 sets the spot count on a group service without prices.
  function bokameraSetPriceQuantity(priceId, quantity) {
    bm('setPriceQuantity', '${configId}', { priceId: priceId, quantity: quantity });
  }
  ${buildSetPriceQuantityExample(priceId)}`;

const buildSetPriceQuantityExample = (priceId: number | null): string => {
    if (priceId === null) {
        return `// Example: bokameraSetPriceQuantity(${UNPRICED_PRICE_ID}, ${PLACEHOLDER_SPOT_COUNT}); // 0 = a group service without prices; replace with one of your price ids`;
    }
    return `// Example: bokameraSetPriceQuantity(${priceId}, 1);`;
};

const buildSyncedAddOnsListener = (configId: string, syncedArticleIds: number[]): string =>
    `  // Keep a group of checkout add-ons at the same quantity, in either direction:
  // change one (in the widget checkout or via a host command) and the others follow.
  // Only service add-ons (ArticleTypeId 6, Duration 0) take part in the event/command.
  ${buildSyncedIdsDeclaration(syncedArticleIds)}
  var bokameraKnownQuantities = {};
  bokameraSyncedArticleIds.forEach(function (id) { bokameraKnownQuantities[id] = 0; });

  function bokameraQuantityOf(articles, id) {
    var match = articles.filter(function (article) { return article.ArticleId === id; })[0];
    return match ? match.Quantity : 0;
  }

  bm('on', '${configId}', 'selectedArticlesChange', function (payload) {
    var changedId = bokameraSyncedArticleIds.filter(function (id) {
      return bokameraQuantityOf(payload.articles, id) !== bokameraKnownQuantities[id];
    })[0];
    bokameraSyncedArticleIds.forEach(function (id) {
      bokameraKnownQuantities[id] = bokameraQuantityOf(payload.articles, id);
    });
    if (changedId === undefined) return;
    var quantity = bokameraKnownQuantities[changedId];
    bokameraSyncedArticleIds.forEach(function (id) {
      if (id === changedId || bokameraKnownQuantities[id] === quantity) return;
      bm('setArticleQuantity', '${configId}', { articleId: id, quantity: quantity });
    });
  });`;

const buildSyncedIdsDeclaration = (syncedArticleIds: number[]): string => {
    const ids = resolveSyncedArticleIds(syncedArticleIds).join(', ');
    if (syncedArticleIds.length < MIN_SYNCED_ARTICLES) {
        return `var bokameraSyncedArticleIds = [${ids}]; // replace with your add-on article ids`;
    }
    return `var bokameraSyncedArticleIds = [${ids}];`;
};

const buildSyncedPricesListener = (configId: string, syncedPriceIds: number[]): string =>
    `  // Keep a group of price counters at the same quantity, in either direction:
  // change one (in the widget checkout or via a host command) and the others follow.
  // Counter-mode services only: two or more prices AND a spot range (group booking
  // or multiple resources). A price-only service shows radios and a service without
  // prices has a single #0 count, so there is nothing to mirror there.
  // The widget rejects a follow-up that exceeds the service maximum or the spots
  // left on the chosen time (see its console); the others keep their quantity.
  ${buildSyncedPriceIdsDeclaration(syncedPriceIds)}
  var bokameraKnownPriceQuantities = {};
  bokameraSyncedPriceIds.forEach(function (id) { bokameraKnownPriceQuantities[id] = 0; });

  function bokameraPriceQuantityOf(quantities, id) {
    var match = quantities.filter(function (row) { return row.PriceId === id; })[0];
    return match ? match.Quantity : 0;
  }

  bm('on', '${configId}', 'priceQuantitiesChange', function (payload) {
    var changedId = bokameraSyncedPriceIds.filter(function (id) {
      return bokameraPriceQuantityOf(payload.quantities, id) !== bokameraKnownPriceQuantities[id];
    })[0];
    bokameraSyncedPriceIds.forEach(function (id) {
      bokameraKnownPriceQuantities[id] = bokameraPriceQuantityOf(payload.quantities, id);
    });
    if (changedId === undefined) return;
    var quantity = bokameraKnownPriceQuantities[changedId];
    bokameraSyncedPriceIds.forEach(function (id) {
      if (id === changedId || bokameraKnownPriceQuantities[id] === quantity) return;
      bm('setPriceQuantity', '${configId}', { priceId: id, quantity: quantity });
    });
  });`;

const buildSyncedPriceIdsDeclaration = (syncedPriceIds: number[]): string => {
    const ids = resolveSyncedPriceIds(syncedPriceIds).join(', ');
    if (syncedPriceIds.length < MIN_SYNCED_PRICES) {
        return `var bokameraSyncedPriceIds = [${ids}]; // replace with price ids from one service`;
    }
    return `var bokameraSyncedPriceIds = [${ids}];`;
};

const buildSetCustomerDataCommand = (configId: string, customer: CustomerData): string => {
    const lines = buildCustomerFieldLines(customer);
    if (lines.length === 0) return `  bm('setCustomerData', '${configId}', {});`;
    return `  bm('setCustomerData', '${configId}', {\n${indentLines(lines)}\n  });`;
};

const buildCustomerFieldLines = (customer: CustomerData): string[] =>
    CUSTOMER_FIELDS.map((field) => buildCustomerFieldLine(field, customer[field])).filter(
        isPresent
    );

const buildCustomerFieldLine = (field: keyof CustomerData, value?: string): string | null => {
    const trimmed = value?.trim() ?? '';
    return trimmed ? `${field}: '${escapeJsString(trimmed)}',` : null;
};

const indentLines = (lines: string[]): string => lines.map((line) => `    ${line}`).join('\n');

const isPresent = (line: string | null): line is string => line !== null;

export const escapeJsString = (value: string): string =>
    value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
